/**
 * AppointmentProviderFilter — the speciality chip row + horizontal
 * provider/doctor facet cards above the patient appointments table.
 *
 * Flutter ref: `patient_appointments_tab.dart`
 *   - `specialitiesView()` (:1052) — "All" + one chip per speciality,
 *     single-select. Specialities are derived from the facet providers'
 *     `doctorSpecialities` (dedupe by `specialitySkillId`, sort by label).
 *   - `doctorListWidget()` / `doctorItemView()` (:883) — horizontal scroll
 *     of provider cards: avatar + name + "{speciality} • N upcoming
 *     appointments" + a round checkbox that toggles the provider into the
 *     `practitioners` request filter. Cards are filtered by the selected
 *     speciality chip (`doctorSpecialityIds.contains(sel)`).
 *
 * Speciality selection is a CLIENT-side filter (Flutter `loadAppointmentList`)
 * — it narrows the visible cards and rows but is NOT sent to the server.
 * Toggling a provider checkbox DOES refetch (server `practitioners[]`).
 */

import { Avatar, Checkbox, Segmented } from '@scripsteam/scrips-signal-ds';
import { cn } from '@/lib/utils';
import type { AppointmentFacetProvider } from '@/types/scheduling-api';

export interface SpecialityOption {
  /** `specialitySkillId` */
  id: string;
  label: string;
}

/**
 * Sentinel value for the "All" segment. The speciality filter models
 * "no speciality selected" as `null` (Flutter `selectedSpeciality == null`),
 * but Segmented is value-based and single-select — so "All" carries this
 * sentinel and is mapped back to `null` in onChange.
 *
 * Contract (verified against scrips-signal-ds@1.5.0 source): Segmented's
 * `select(v)` calls `onChange?.(v)` with the option's `value` VERBATIM — it
 * does NOT coerce empty/sentinel values to null/undefined internally. So the
 * `'__all__'` sentinel round-trips intact and we own the null mapping here.
 * Pinned by the "maps '__all__' back to null" test.
 */
const ALL_SPECIALITIES = '__all__';

/**
 * Unique speciality options across all facet providers' `doctorSpecialities`,
 * sorted by label (matches Flutter's fold-by-specialitySkillId + sort).
 */
export function deriveSpecialities(
  providers: AppointmentFacetProvider[],
): SpecialityOption[] {
  const map = new Map<string, string>();
  for (const p of providers) {
    for (const s of p.doctorSpecialities ?? []) {
      const id = s.specialitySkillId;
      if (id && !map.has(id)) {
        map.set(id, s.code?.displayName ?? 'N/A');
      }
    }
  }
  return Array.from(map, ([id, label]) => ({ id, label })).sort((a, b) =>
    a.label.toLowerCase().localeCompare(b.label.toLowerCase()),
  );
}

interface AppointmentProviderFilterProps {
  providers: AppointmentFacetProvider[];
  selectedSpeciality: string | null;
  onSelectSpeciality: (id: string | null) => void;
  selectedPractitioners: string[];
  onTogglePractitioner: (id: string) => void;
}

export function AppointmentProviderFilter({
  providers,
  selectedSpeciality,
  onSelectSpeciality,
  selectedPractitioners,
  onTogglePractitioner,
}: AppointmentProviderFilterProps) {
  const specialities = deriveSpecialities(providers);

  const visibleProviders = selectedSpeciality
    ? providers.filter((p) =>
        (p.doctorSpecialityIds ?? []).includes(selectedSpeciality),
      )
    : providers;

  // Nothing to choose between: with one practitioner (a physician's patients usually show only the physician) the
  // speciality row and the lone card with its checkbox filter nothing, so the filter is not drawn (consistency pass,
  // 15 Oct, the calendar's rule: "does it make sense to have … my own person?"). Two or more: as before.
  if (providers.length <= 1) return null;

  return (
    <div className="space-y-3">
      {specialities.length > 0 ? (
        <Segmented
          aria-label="Filter by speciality"
          options={[
            { value: ALL_SPECIALITIES, label: 'All' },
            ...specialities.map((s) => ({ value: s.id, label: s.label })),
          ]}
          value={selectedSpeciality ?? ALL_SPECIALITIES}
          onChange={(v) =>
            onSelectSpeciality(v === ALL_SPECIALITIES ? null : v)
          }
        />
      ) : null}

      <div className="flex gap-0 overflow-x-auto scrollbar-hide border-b border-border-default pb-2">
        {visibleProviders.map((p, i) => (
          <div
            key={p.id}
            className={cn(
              'flex w-62.5 shrink-0 items-start gap-3 px-4',
              i > 0 && 'border-l border-border-default',
            )}
          >
            <Avatar
              size="sm"
              src={p.photo || undefined}
              name={p.name ?? undefined}
              initials={initials(p.name)}
              alt=""
            />
            <div className="min-w-0 flex-1">
              <div className="flex items-start gap-2">
                <span className="min-w-0 flex-1 truncate text-sm font-bold text-text-primary">
                  {p.name ?? 'N/A'}
                </span>
                <Checkbox
                  checked={selectedPractitioners.includes(p.id)}
                  onCheckedChange={() => onTogglePractitioner(p.id)}
                  // The provider's name is already visible as a sibling <span>,
                  // so this control needs an invisible accessible name, not a
                  // second visible one. This briefly used an `sr-only` node
                  // inside the `label` prop as a workaround, because the DS
                  // Checkbox had no aria-* passthrough. It gained one on
                  // 2026-09-20, so the real mechanism is used here instead.
                  aria-label={`Filter by ${p.name ?? 'provider'}`}
                  className="mt-1"
                />
              </div>
              <p className="mt-1 line-clamp-2 text-xs text-text-muted">
                {p.speciality?.code?.displayName ?? 'N/A'} •{' '}
                {p.upcommingAppointment ?? 0} upcoming appointments
              </p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function initials(name?: string): string {
  if (!name) return '—';
  const parts = name.trim().split(/\s+/);
  return (parts[0]?.[0] ?? '') + (parts[1]?.[0] ?? '');
}
